/**
 * InsForge provider — real InsForge SDK for active backend-boundary behavior.
 *
 * Data operations (from, rpc) use the InsForge SDK directly via @insforge/sdk's
 * PostgREST-compatible query builder. This is the only backend provider
 * (ADR-0002), so an unsupported SDK gap must surface explicitly rather than
 * degrade into a silent fallback.
 */

import { isQueryAnalysisEnabled, queryObserver } from "@/lib/db/query-observer";
import { insforge } from "@/lib/insforge";
import { createResilientCall } from "@/lib/resilience/circuit-breaker";
import { observeDataClient } from "./observed-data-client";
import type {
  BackendClient,
  DataClient,
  FunctionsClient,
  QueryBuilder,
  QueryResult,
  Row,
  StorageClient,
  TableName,
} from "./types";

// ---------------------------------------------------------------------------
// Data — real InsForge SDK (PostgREST-compatible)
// ---------------------------------------------------------------------------

const insforgeData: DataClient = {
  from<T extends TableName>(table: T): QueryBuilder<Row<T>> {
    return insforge.database.from(table) as unknown as QueryBuilder<Row<T>>;
  },

  async rpc<TResult = unknown>(
    fn: string,
    params?: Record<string, unknown>,
    options?: { count?: "exact" | "planned" | "estimated"; head?: boolean },
  ): Promise<QueryResult<TResult>> {
    const { data, error, count } = await insforge.database.rpc(fn, params, options);
    return {
      data: data as TResult | null,
      error: error ? { message: error.message, code: error.code, details: error.details } : null,
      count,
    };
  },
};

// N+1 and slow-query analysis wraps the raw SDK adapter in development and
// under test; production bundles use the adapter directly unless
// VITE_DB_QUERY_ANALYSIS opts in. See src/lib/db/query-observer.ts.
const observedInsforgeData: DataClient = isQueryAnalysisEnabled()
  ? observeDataClient(insforgeData, queryObserver)
  : insforgeData;

// ---------------------------------------------------------------------------
// Functions — InsForge SDK (with circuit breaker)
// ---------------------------------------------------------------------------

const resilientInvoke = createResilientCall(
  (
    functionName: string,
    options?: {
      body?: unknown;
      headers?: Record<string, string>;
      method?: "DELETE" | "GET" | "PATCH" | "POST" | "PUT";
    },
  ) => insforge.functions.invoke(functionName, options),
  { breakerThreshold: 5, halfOpenAfter: 30_000, maxRetries: 3 },
);

/**
 * The SDK interpolates the slug straight into the function URL, so query
 * parameters ride along with it — there is no separate options field for them.
 */
function withQuery(functionName: string, query?: Record<string, string>): string {
  if (!query) {
    return functionName;
  }
  const search = new URLSearchParams(query).toString();
  return search ? `${functionName}?${search}` : functionName;
}

const insforgeFunctions: FunctionsClient = {
  async invokeRaw(functionName, { body, signal, accept = "application/json" }) {
    if (!/^[a-z][a-z0-9-]*$/.test(functionName)) throw new Error("Invalid function name");
    const http = insforge.getHttpClient();
    // The hosted /functions proxy buffers the whole response before sending
    // headers, so the SDK's 30s request timeout would abort any longer turn
    // while the function keeps committing. Fetch directly with SDK auth: the
    // caller's signal is the only deadline, and a 401 is never replayed.
    const headers = new Headers(http.getHeaders());
    headers.set("Content-Type", "application/json");
    headers.set("Accept", accept);
    return http.fetch(`${http.baseUrl.replace(/\/+$/, "")}/functions/${functionName}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal,
      redirect: "error",
    });
  },
  async invoke<TResult = unknown>(
    functionName: string,
    options?: {
      retry?: false;
      body?: unknown;
      headers?: Record<string, string>;
      method?: "DELETE" | "GET" | "PATCH" | "POST" | "PUT";
      query?: Record<string, string>;
    },
  ): Promise<QueryResult<TResult>> {
    const invoke =
      options?.retry === false
        ? insforge.functions.invoke.bind(insforge.functions)
        : resilientInvoke;
    const { data, error } = await invoke(withQuery(functionName, options?.query), {
      body: options?.body,
      headers: options?.headers,
      method: options?.method,
    });
    return {
      data: data as TResult | null,
      // @insforge/sdk maps non-2xx JSON through InsForgeError. Older edge
      // envelopes used `{ error: "human message" }`, which leaves the native
      // Error.message empty and keeps the useful value on InsForgeError.error.
      // Preserve both SDK generations at our provider boundary so callers can
      // still classify definitive failures.
      error: error
        ? {
            message:
              (typeof error.message === "string" && error.message.trim()) ||
              ("error" in error && typeof error.error === "string" && error.error.trim()) ||
              "Function invocation failed",
            ...("error" in error && typeof error.error === "string" ? { code: error.error } : {}),
            ...("statusCode" in error && typeof error.statusCode === "number"
              ? { statusCode: error.statusCode }
              : {}),
          }
        : null,
    };
  },
};

// ---------------------------------------------------------------------------
// Storage — InsForge SDK (download resolves the signed URL, avoiding the
// storage endpoint's cross-origin 302 that browser fetch refuses to follow)
// ---------------------------------------------------------------------------

const insforgeStorage: StorageClient = {
  from(bucket: string) {
    const bucketClient = insforge.storage.from(bucket);
    return {
      async createSignedUrl(objectKey: string, expiresIn?: number) {
        const { data, error } = await bucketClient.createSignedUrl(objectKey, expiresIn);
        return {
          data,
          error: error ? { message: error.message } : null,
        };
      },
      async download(objectKey: string): Promise<QueryResult<Blob>> {
        const { data, error } = await bucketClient.download(objectKey);
        return {
          data,
          error: error ? { message: error.message } : null,
        };
      },
      async remove(objectKey: string) {
        const { data, error } = await bucketClient.remove(objectKey);
        return {
          data,
          error: error ? { message: error.message } : null,
        };
      },
      async upload(objectKey: string, file: Blob | File) {
        const { data, error } = await bucketClient.upload(objectKey, file);
        return {
          data: data
            ? {
                key: data.key,
                url: data.url,
                size: data.size,
                mimeType: data.mimeType,
              }
            : null,
          error: error ? { message: error.message } : null,
        };
      },
    };
  },
};

// ---------------------------------------------------------------------------
// Composite client
// ---------------------------------------------------------------------------

export const insforgeProvider: BackendClient = {
  data: observedInsforgeData,
  functions: insforgeFunctions,
  storage: insforgeStorage,
};
